import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AdminModal, AdminField, ConfirmModal } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoDownloadOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoAddOutline,
  IoWarningOutline
} from 'react-icons/io5';
import AdminDataTable from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useLatestRequest, useFirstLoad
} from '../../components/admin/useAdminTable';
import EntityPicker from '../../components/admin/EntityPicker';
import { searchReaders, searchFamilies } from '../../components/admin/pickerSearches';
import './AdminReportsPage.css';

const EMPTY_FILTERS = { startDate: '', endDate: '', readerId: '', familyId: '' };
const EMPTY_LABELS = { readerId: '', familyId: '' };

// A failed blob download carries its JSON error as a Blob
const exportErrorMessage = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.error) return parsed.error;
    } catch {
      // not JSON; fall through
    }
  }
  return apiErrorMessage(err, 'Failed to export reports.');
};

const ReportEditModal = ({ report, onClose, onSaved }) => {
  const initial = {
    chapterId: report.chapterId ?? '',
    readDate: report.readDate ?? '',
    notes: report.notes ?? '',
    readingMethod: report.readingMethod ?? ''
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const changes = {};
    if (String(form.chapterId) !== String(initial.chapterId)) changes.chapterId = Number(form.chapterId);
    if (form.readDate !== initial.readDate) changes.readDate = form.readDate;
    if (form.notes !== initial.notes) changes.notes = form.notes || null;
    if (form.readingMethod !== initial.readingMethod) changes.readingMethod = form.readingMethod || null;

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const result = await adminApi.updateReport(report.id, changes);
      const resynced = result.data.resync?.resynced;
      onSaved(`Updated the report${resynced ? ', and resynced the reader\'s completions' : ''}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this report.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title="Edit report"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="report-edit-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        Reader: {report.reader?.name || report.readerId}
      </p>

      <form id="report-edit-form" onSubmit={handleSubmit}>
        <AdminField
          label="Chapter id"
          hint="Changing the chapter or the date re-derives this reader's Bible completions."
        >
          <input type="number" min="1" value={form.chapterId} onChange={set('chapterId')} required />
        </AdminField>
        <AdminField label="Read date">
          <input type="date" value={form.readDate} onChange={set('readDate')} required />
        </AdminField>
        <AdminField label="Reading method">
          <select value={form.readingMethod} onChange={set('readingMethod')}>
            <option value="">—</option>
            <option value="paper">paper</option>
            <option value="digital">digital</option>
            <option value="audio">audio</option>
            <option value="mixed">mixed</option>
          </select>
        </AdminField>
        <AdminField label="Notes">
          <textarea value={form.notes} onChange={set('notes')} rows={2} />
        </AdminField>
      </form>
    </AdminModal>
  );
};

const LogReadingModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ readerId: '', readerLabel: '', chapterIds: '', readDate: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const chapterIds = form.chapterIds
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    if (chapterIds.some((id) => !Number.isInteger(id) || id < 1)) {
      setError('Chapter ids must be positive whole numbers, separated by commas or spaces.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const result = await adminApi.createReport({
        readerId: form.readerId,
        chapterIds,
        ...(form.readDate ? { readDate: form.readDate } : {}),
        ...(form.notes ? { notes: form.notes } : {})
      });
      onSaved(`Logged ${result.data.reports.length} chapter(s) on the reader's behalf`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not log this reading.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title="Log reading on a reader's behalf"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="submit"
            form="log-reading-form"
            className="admin-modal-btn is-primary"
            disabled={saving || !form.readerId}
          >
            {saving ? 'Logging…' : 'Log reading'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        The report is recorded against the reader&apos;s own account, not yours, and their Bible
        completions are re-derived afterwards.
      </p>

      <form id="log-reading-form" onSubmit={handleSubmit}>
        <AdminField label="Reader" hint="Search by reader name.">
          <EntityPicker
            ariaLabel="reader"
            placeholder="Reader name"
            value={form.readerId}
            valueLabel={form.readerLabel}
            search={searchReaders}
            onChange={(readerId, readerLabel) => setForm((prev) => ({ ...prev, readerId, readerLabel }))}
          />
        </AdminField>

        <AdminField
          label="Chapter ids"
          hint="Comma or space separated, for example: 1, 2, 3"
        >
          <input type="text" value={form.chapterIds} onChange={set('chapterIds')} required />
        </AdminField>
        <AdminField label="Read date" hint="Defaults to today.">
          <input type="date" value={form.readDate} onChange={set('readDate')} />
        </AdminField>
        <AdminField label="Notes">
          <textarea value={form.notes} onChange={set('notes')} rows={2} />
        </AdminField>
      </form>
    </AdminModal>
  );
};

/**
 * Scoped bulk delete. Preview is mandatory: the count it returns is sent back
 * with the delete, and the API refuses to run if the number has moved.
 */
const BulkDeleteModal = ({ filters, labels, onClose, onDone }) => {
  const [scope, setScope] = useState({
    readerId: filters.readerId || '',
    familyId: filters.familyId || '',
    startDate: filters.startDate || '',
    endDate: filters.endDate || ''
  });
  const [scopeLabels, setScopeLabels] = useState(labels);

  const pick = (field) => (id, label) => {
    setScope((prev) => ({ ...prev, [field]: id }));
    setScopeLabels((prev) => ({ ...prev, [field]: label }));
    setPreview(null);
  };
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    setScope((prev) => ({ ...prev, [field]: event.target.value }));
    setPreview(null);
  };

  const payload = () => ({
    ...(scope.readerId ? { readerId: scope.readerId } : {}),
    ...(scope.familyId ? { familyId: scope.familyId } : {}),
    ...(scope.startDate ? { startDate: scope.startDate } : {}),
    ...(scope.endDate ? { endDate: scope.endDate } : {})
  });

  const handlePreview = async () => {
    try {
      setBusy(true);
      setError(null);
      const response = await adminApi.previewBulkDeleteReports(payload());
      setPreview(response.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not preview this delete.'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      setBusy(true);
      setError(null);
      const response = await adminApi.bulkDeleteReports({ ...payload(), expectedCount: preview.count });
      onDone(`Deleted ${response.data.deletedCount} report(s) and resynced ${response.data.resyncedReaders} reader(s)`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete these reports.'));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminModal
      title="Delete reports by scope"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={busy}>Cancel</button>
          {!preview ? (
            <button type="button" className="admin-modal-btn is-primary" onClick={handlePreview} disabled={busy}>
              {busy ? 'Counting…' : 'Preview'}
            </button>
          ) : (
            <button type="button" className="admin-modal-btn is-danger" onClick={handleDelete} disabled={busy || preview.count === 0}>
              {busy ? 'Deleting…' : `Delete ${preview.count} report(s)`}
            </button>
          )}
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        At least one filter is required — deleting every report is deliberately not possible here.
        Readers whose reports are removed have their Bible completions and badges re-derived
        afterwards.
      </p>

      <AdminField label="Reader">
        <EntityPicker
          ariaLabel="reader"
          placeholder="Reader name"
          value={scope.readerId}
          valueLabel={scopeLabels.readerId}
          search={searchReaders}
          onChange={pick('readerId')}
        />
      </AdminField>
      <AdminField label="Family" hint="Scopes to every reader in that family.">
        <EntityPicker
          ariaLabel="family"
          placeholder="Family name"
          value={scope.familyId}
          valueLabel={scopeLabels.familyId}
          search={searchFamilies}
          onChange={pick('familyId')}
        />
      </AdminField>
      <AdminField label="From (read date)">
        <input type="date" value={scope.startDate} onChange={set('startDate')} />
      </AdminField>
      <AdminField label="Through (read date)">
        <input type="date" value={scope.endDate} onChange={set('endDate')} />
      </AdminField>

      {preview && (
        <div className={preview.count === 0 ? 'admin-modal-notice' : 'admin-modal-drift'}>
          <h4><IoWarningOutline /> {preview.count} report(s) would be deleted</h4>
          <p>
            Scoped by {preview.scope}, across {preview.affectedReaderCount} reader(s).
            {preview.count > 0 && ' This cannot be undone.'}
          </p>
          {preview.sample?.length > 0 && (
            <ul className="admin-modal-drift-list">
              {preview.sample.slice(0, 5).map((row) => (
                <li key={row.id}>
                  {row.reader?.name || row.readerId} — chapter {row.chapterId} on {row.readDate}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </AdminModal>
  );
};

const AdminReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterLabels, setFilterLabels] = useState(EMPTY_LABELS);
  const [currentPage, setCurrentPage] = useState(1);
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);

  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [logging, setLogging] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [busy, setBusy] = useState(false);
  // Errors from a confirm dialog's action show inside that dialog, not behind it
  const [dialogError, setDialogError] = useState(null);

  const load = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      // Settled separately: a stats failure should not empty the list.
      const [reportsResult, statsResult] = await Promise.allSettled([
        adminApi.getReports({ page: currentPage, limit: 50, ...filters }),
        adminApi.getReportsStats(filters)
      ]);
      if (!request.isCurrent()) return;
      if (reportsResult.status === 'rejected') throw reportsResult.reason;
      setReports(reportsResult.value.data.reports || []);
      setPagination(reportsResult.value.data.pagination || {});
      setStats(statsResult.status === 'fulfilled' ? statsResult.value.data : null);
      setError(null);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(apiErrorMessage(err, 'Failed to load reports.'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
    setCurrentPage(1);
  };

  const pickFilter = (field) => (id, label) => {
    setFilters((prev) => ({ ...prev, [field]: id }));
    setFilterLabels((prev) => ({ ...prev, [field]: label }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setFilterLabels(EMPTY_LABELS);
    setCurrentPage(1);
  };

  const closeModals = () => {
    setEditing(null);
    setDeleting(null);
    setLogging(false);
    setBulkDeleting(false);
    setResetting(null);
    setDialogError(null);
  };

  const handleDone = (message) => {
    closeModals();
    setNotice(message);
    load();
  };

  const handleExport = async () => {
    try {
      const response = await adminApi.exportReports(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reading-reports-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setNotice(null);
      setError(await exportErrorMessage(err));
    }
  };

  const handleDeleteOne = async () => {
    try {
      setBusy(true);
      setDialogError(null);
      const result = await adminApi.deleteReport(deleting.id);
      handleDone(`Deleted the report${result.data.resync?.resynced ? ' and resynced the reader' : ''}`);
    } catch (err) {
      setDialogError(apiErrorMessage(err, 'Could not delete this report.'));
    } finally {
      setBusy(false);
    }
  };

  // The site-wide resets. Kept, because recovering a bad import still needs
  // them, but they are no longer the only bulk option and no longer live behind
  // a window.prompt.
  const RESETS = {
    reports: {
      title: 'Delete every reading report',
      phrase: 'DELETE_ALL_REPORTS',
      body: 'This removes every reading report for every reader, site-wide. Bible completions, reading plan progress and earned badges are NOT cleaned up by this, so it leaves derived data pointing at reports that no longer exist. Prefer a scoped delete unless you are resetting the whole site.',
      run: () => adminApi.resetAllReports()
    },
    progress: {
      title: 'Reset all progress and points',
      phrase: 'RESET_ALL_PROGRESS',
      body: 'This wipes reports, points transactions, completions, plan progress and badges for every reader, and zeroes point totals. There is no undo.',
      run: () => adminApi.resetAllProgress('RESET_ALL_PROGRESS')
    },
    fake: {
      title: 'Remove seeded test data',
      phrase: 'DELETE_ALL_FAKE_DATA',
      body: 'This removes data created by the fake-data seeder. Real user data is left alone.',
      run: () => adminApi.cleanupFakeData()
    }
  };

  const selection = useRowSelection(`${currentPage}|${filters.startDate}|${filters.endDate}|${filters.readerId}|${filters.familyId}`);
  const rowBulk = useBulkAction((_result, text) => {
    selection.clear();
    setNotice(text);
    load();
  });

  const handleReset = async () => {
    try {
      setBusy(true);
      setDialogError(null);
      await RESETS[resetting].run();
      handleDone(`${RESETS[resetting].title} — done`);
    } catch (err) {
      setDialogError(apiErrorMessage(err, 'That reset did not complete.'));
    } finally {
      setBusy(false);
    }
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading reports..." />
      </div>
    );
  }

  return (
    <div className="admin-reports-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Reading Reports</h1>
          <p className="admin-page-description">
            Correct or remove individual reports, log reading on a reader&apos;s behalf, and delete
            by scope
          </p>
        </div>
        <div className="reports-header-actions">
          <button className="report-primary-btn" onClick={() => setLogging(true)}>
            <IoAddOutline /> Log Reading
          </button>
          <button className="report-secondary-btn" onClick={handleExport}>
            <IoDownloadOutline /> Export CSV
          </button>
        </div>
      </div>

      {notice && (
        <div className="report-success-banner">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)}>&times;</button>
        </div>
      )}

      {stats && (
        <div className="admin-card report-stats">
          <div><div className="stat-number">{stats.totalReports}</div><div className="stat-label">Reports</div></div>
          <div><div className="stat-number">{stats.uniqueReaders}</div><div className="stat-label">Readers</div></div>
          <div><div className="stat-number">{stats.uniqueChapters}</div><div className="stat-label">Chapters</div></div>
        </div>
      )}

      <div className="admin-card">
        <div className="report-filters">
          <label>
            <span>From</span>
            <input type="date" value={filters.startDate} onChange={setFilter('startDate')} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={filters.endDate} onChange={setFilter('endDate')} />
          </label>
          <div className="report-filter-field">
            <span>Reader</span>
            <EntityPicker
              ariaLabel="reader filter"
              placeholder="Reader name"
              value={filters.readerId}
              valueLabel={filterLabels.readerId}
              search={searchReaders}
              onChange={pickFilter('readerId')}
            />
          </div>
          <div className="report-filter-field">
            <span>Family</span>
            <EntityPicker
              ariaLabel="family filter"
              placeholder="Family name"
              value={filters.familyId}
              valueLabel={filterLabels.familyId}
              search={searchFamilies}
              onChange={pickFilter('familyId')}
            />
          </div>
          <button className="report-secondary-btn" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>

      <AdminDataTable
        title={`Reports (${pagination.totalReports ?? reports.length})`}
        headerAside={(
          <button type="button" className="report-danger-btn" onClick={() => setBulkDeleting(true)}>
            <IoTrashOutline /> Delete by scope
          </button>
        )}
        rows={reports}
        getRowLabel={(report) => report.reader?.name || 'report'}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={load}
        emptyTitle="No reports"
        emptyMessage="No reports match these filters."
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          {
            label: 'Delete selected',
            danger: true,
            onClick: (rows) => rowBulk.ask({
              title: `Delete ${rows.length} report${rows.length === 1 ? '' : 's'}`,
              confirmLabel: 'Delete reports',
              body: (
                <p className="admin-modal-hint">
                  Each selected report is removed and that reader’s Bible completions are re-derived.
                  This cannot be undone.
                </p>
              ),
              run: () => runBulk(rows, (row) => adminApi.deleteReport(row.id)),
              summarize: (result) => summarizeBulk('Deleted', 'report', result)
            })
          }
        ]}
        columns={[
          {
            key: 'reader',
            header: 'Reader',
            render: (report) => (
              <span className="admin-cell-strong">
                {report.reader?.name || '—'}
                <span className="admin-cell-sub">{report.reader?.user?.email || ''}</span>
              </span>
            )
          },
          {
            key: 'chapter',
            header: 'Chapter',
            render: (report) => report.chapter
              ? `${report.chapter.book} ${report.chapter.chapter}`
              : report.chapterId
          },
          { key: 'readDate', header: 'Read date', render: (report) => report.readDate },
          {
            key: 'logged',
            header: 'Logged',
            render: (report) => new Date(report.createdAt).toLocaleDateString()
          }
        ]}
        renderActions={(report) => (
          <>
            <button type="button" onClick={() => setEditing(report)} title="Edit report">
              <IoCreateOutline /> Edit
            </button>
            <button type="button" className="is-danger" onClick={() => setDeleting(report)} title="Delete report">
              <IoTrashOutline /> Delete
            </button>
          </>
        )}
      />
      <BulkActionHost bulk={rowBulk} />

      {/* Collapsed by default: these act on every reader and are rarely the right tool */}
      <details className="admin-card report-danger-zone">
        <summary className="admin-card-title">Danger zone: site-wide resets</summary>
        <p>
          These act on every reader. A scoped delete is almost always the better tool, and unlike
          these it keeps derived data consistent.
        </p>
        <div className="report-danger-actions">
          <button className="report-danger-btn" onClick={() => setResetting('reports')}>
            Delete all reports
          </button>
          <button className="report-danger-btn" onClick={() => setResetting('progress')}>
            Reset all progress
          </button>
          <button className="report-secondary-btn" onClick={() => setResetting('fake')}>
            Remove seeded test data
          </button>
        </div>
      </details>

      {editing && <ReportEditModal report={editing} onClose={closeModals} onSaved={handleDone} />}
      {logging && <LogReadingModal onClose={closeModals} onSaved={handleDone} />}
      {bulkDeleting && (
        <BulkDeleteModal filters={filters} labels={filterLabels} onClose={closeModals} onDone={handleDone} />
      )}

      {deleting && (
        <ConfirmModal
          title="Delete this report"
          confirmLabel="Delete report"
          busy={busy}
          error={dialogError}
          onCancel={closeModals}
          onConfirm={handleDeleteOne}
        >
          <p className="admin-modal-hint">
            Removing chapter {deleting.chapterId} read on {deleting.readDate} by{' '}
            {deleting.reader?.name || 'this reader'}. Their Bible completions are re-derived
            afterwards.
          </p>
        </ConfirmModal>
      )}

      {resetting && (
        <ConfirmModal
          title={RESETS[resetting].title}
          confirmLabel={RESETS[resetting].title}
          confirmPhrase={RESETS[resetting].phrase}
          busy={busy}
          error={dialogError}
          onCancel={closeModals}
          onConfirm={handleReset}
        >
          <p className="admin-modal-hint">{RESETS[resetting].body}</p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default AdminReportsPage;
